import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Grid,
  Paper,
} from '@mui/material';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { Matiere } from '../../types';
import { formatDate } from '../../utils/dates';

interface MatiereDetailProps {
  matiere: Matiere;
}

const MatiereDetail: React.FC<MatiereDetailProps> = ({ matiere }) => {
  return (
    <Box>
      {/* En-tête */}
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          {matiere.nom}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
          <Chip
            label={matiere.nomCourt}
            color="primary"
            size="small"
            variant="outlined"
          />
          <Chip
            label={matiere.code}
            color="secondary"
            size="small"
            variant="outlined"
          />
          <Chip
            label={(matiere.statut || 'published') === 'draft' ? '🔸 Brouillon' : '✅ Publié'}
            color={(matiere.statut || 'published') === 'draft' ? 'warning' : 'success'}
            size="small"
          />
          <Chip
            label={matiere.estActif ? 'Actif' : 'Inactif'}
            color={matiere.estActif ? 'success' : 'default'}
            size="small"
            icon={matiere.estActif ? <CheckCircle /> : <RadioButtonUnchecked />}
          />
          <Chip
            label={`Ordre: ${matiere.ordre}`}
            color="default"
            size="small"
            variant="outlined"
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Informations générales */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Informations générales
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {matiere.id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Code
                </Typography>
                <Typography variant="body2">{matiere.code}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Nom court
                </Typography>
                <Typography variant="body2">{matiere.nomCourt}</Typography>
              </Box>
              {matiere.classeId && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Classe
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {matiere.classeId}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Ordre
                </Typography>
                <Typography variant="body2">{matiere.ordre}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Couleur thème
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: matiere.couleurTheme,
                      borderRadius: 1,
                      border: '1px solid #ccc'
                    }}
                  />
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {matiere.couleurTheme}
                  </Typography>
                </Box>
              </Box>
              {matiere.icone && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Icône
                  </Typography>
                  <Typography variant="h4">{matiere.icone}</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Dates
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Date de création
                </Typography>
                <Typography variant="body2">
                  {formatDate(matiere.dateCreation, 'dayMonthLong')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Dernière modification
                </Typography>
                <Typography variant="body2">
                  {formatDate(matiere.dateModification, 'dayMonthLong')}
                </Typography>
              </Box>
              {matiere.datePublication && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Date de publication
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(matiere.datePublication, 'dayMonthLong')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Historique des statuts */}
      {matiere.historiqueStatut && matiere.historiqueStatut.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Historique des statuts
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            {matiere.historiqueStatut.map((entry, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  borderLeft: 4,
                  borderColor: entry.statut === 'published' ? 'success.main' : 'warning.main'
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Chip
                    label={entry.statut === 'draft' ? '🔸 Brouillon' : '✅ Publié'}
                    color={entry.statut === 'draft' ? 'warning' : 'success'}
                    size="small"
                  />
                  <Typography variant="body2" color="textSecondary">
                    {formatDate(entry.date, 'dateTimeLong')}
                  </Typography>
                </Box>
                {entry.utilisateur && (
                  <Typography variant="body2" color="textSecondary">
                    Par: {entry.utilisateur}
                  </Typography>
                )}
                {entry.commentaire && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {entry.commentaire}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Description */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom color="primary">
          Description
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {matiere.description}
        </Typography>
      </Paper>

      {/* Détails pédagogiques */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Détails pédagogiques
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5}>
              {matiere.heuresParSemaine && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Heures par semaine
                  </Typography>
                  <Typography variant="body2">
                    {matiere.heuresParSemaine} h
                  </Typography>
                </Box>
              )}
              {matiere.coefficientExamen && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Coefficient d'examen
                  </Typography>
                  <Typography variant="body2">
                    {matiere.coefficientExamen}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Compétences clés */}
        {matiere.competencesClés && matiere.competencesClés.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Compétences clés
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {matiere.competencesClés.map((competence, index) => (
                  <Chip
                    key={index}
                    label={competence}
                    variant="outlined"
                    color="primary"
                    size="small"
                  />
                ))}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default MatiereDetail;
